import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiUser, FiMail, FiMapPin, FiKey
} from 'react-icons/fi';
import { API_BASE_URL } from '../../config';
import AdminLayout from './AdminLayout';

const User = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales_person',
    showroom_id: ''
  });
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSalesPerson = currentUser.role === 'sales_person';

  const parseList = (data, key) => {
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (key && data?.[key] && Array.isArray(data[key])) return data[key];
    return [];
  };

  const fetchShowrooms = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_BASE_URL}/api/showrooms`, config);
      setShowrooms(parseList(response.data, 'showrooms'));
    } catch (error) {
      console.error('Showrooms fetch error:', error);
      toast.error('Failed to fetch showrooms');
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const usersResponse = await axios.get(`${API_BASE_URL}/api/users`, config);
      setUsers(parseList(usersResponse.data, 'users'));
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchShowrooms();
  }, [token, navigate, fetchData, fetchShowrooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingItem && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Prepare data - remove password if empty when editing
      const submitData = { ...formData };
      if (editingItem && !submitData.password) {
        delete submitData.password;
      }
      
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/api/auth/users/${editingItem.id}`,
          submitData,
          config
        );
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/api/auth/register`, submitData, config);
        toast.success('User added successfully');
      }
      
      setShowModal(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    // Prevent deleting own account
    if (currentUser.id === id) {
      toast.error('You cannot delete your own account');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_BASE_URL}/api/users/${id}`, config);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      email: item.email || '',
      password: '', // Don't populate password for security
      role: item.role || 'sales_person',
      showroom_id: item.showroom_id || ''
    });
    setShowModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setPasswordData({
      password: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(
        `${API_BASE_URL}/api/users/${selectedUser.id}/password`,
        { password: passwordData.password },
        config
      );
      toast.success('Password updated successfully');
      setShowPasswordModal(false);
      setPasswordData({ password: '', confirmPassword: '' });
      setSelectedUser(null);
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update password');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'sales_person',
      showroom_id: ''
    });
  };

  const getShowroomName = (showroomId) => {
    const showroom = showrooms.find(s => s.id === showroomId);
    return showroom ? showroom.name : 'N/A';
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'supervisor':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-green-100 text-green-700';
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Users Management</h1>
            <p className="text-gray-600 mt-1">Manage system users and their roles</p>
          </div>
          {!isSalesPerson && (
            <button
              onClick={() => {
                setEditingItem(null);
                resetForm();
                setShowModal(true);
              }}
              className="bg-philips-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-philips-dark-blue transition-colors"
            >
              <FiPlus /> Add User
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-philips-blue"></div>
            <p className="mt-2 text-gray-600">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiUser className="mx-auto text-6xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Yet</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first user.</p>
            {!isSalesPerson && (
              <button
                onClick={() => {
                  setEditingItem(null);
                  resetForm();
                  setShowModal(true);
                }}
                className="bg-philips-blue text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-philips-dark-blue"
              >
                <FiPlus /> Add User
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Showroom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <FiUser className="text-blue-600" />
                          </div>
                          <span className="font-medium">{user.name}</span>
                          {user.id === currentUser.id && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">You</span>
                          )}
                        </div>
                       </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FiMail className="text-gray-400" /> {user.email}
                        </div>
                       </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
                          {user.role || 'sales_person'}
                        </span>
                       </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FiMapPin className="text-gray-400" />
                          {user.showroom_id ? getShowroomName(user.showroom_id) : 'All Showrooms'}
                        </div>
                       </td>
                      <td className="px-6 py-4">
                        {!isSalesPerson && (
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>
                        )}
                        {!isSalesPerson && (
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="text-yellow-600 hover:text-yellow-800 mr-2"
                            title="Reset Password"
                          >
                            <FiKey />
                          </button>
                        )}
                        {!isSalesPerson && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                            disabled={user.id === currentUser.id}
                          >
                            <FiTrash2 />
                          </button>
                        )}
                       </td>
                     </tr>
                  ))}
                </tbody>
               </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingItem ? 'Edit User' : 'Add New User'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              
              {!editingItem && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-field"
                    placeholder="Min 6 characters"
                    required={!editingItem}
                  />
                </div>
              )}
              
              {editingItem && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password (Optional)
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-field"
                    placeholder="Leave blank to keep current password"
                  />
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="sales_person">Sales Person</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Showroom</label>
                <select
                  value={formData.showroom_id}
                  onChange={(e) => setFormData({ ...formData, showroom_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">All Showrooms</option>
                  {showrooms.map((showroom) => (
                    <option key={showroom.id} value={showroom.id}>
                      {showroom.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Select "All Showrooms" for system-wide access</p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <FiCheck /> {editingItem ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Reset Password</h2>
              <h3 className="text-md text-gray-600">for {selectedUser.name}</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={24} />
              </button>
            </div>
            
            <form onSubmit={handlePasswordUpdate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password *</label>
                <input
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  className="input-field"
                  placeholder="Min 6 characters"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="input-field"
                  placeholder="Confirm new password"
                  required
                />
              </div>
              
              <div className="flex gap-3 mt-6">
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <FiKey /> Update Password
                </button>
                <button type="button" onClick={() => setShowPasswordModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default User;